<template>
    <div>
        <FullscreenLoading v-if="isLoading" />

        <div class="row">
            <div class="col-12 col-md-4">
                <div class="player-preview-wrapper">
                    <div class="d-flex align-items-center mb-2 mt-3">
                        <h3 class="m-0">{{ $t('controller.current_view') }}</h3>

                        <button class="btn btn-xs btn-outline-primary ml-auto" @click="$emit('change-mode', RENDER_MODE.SIMPLE)">
                            {{ $t('controller.normal_view') }}
                        </button>
                    </div>

                    <div class="embed-responsive embed-responsive-16by9 player-preview">
                        <Player
                            :communication="PLAYER_COMMUNICATION.IPC"
                            :readonly="true"
                            :muted="true"
                            class="embed-responsive-item"
                        />
                    </div>

                    <div class="mt-3 d-flex align-items-center">
                        <button
                            class="btn btn-xs btn-outline-primary mr-auto"
                            v-tooltip="$t('controller.player_position_windows_tooltip')"
                            @click="setPlayerDisplaySettings({ position: PLAYER_POSITION_DISPLAY_SETTINGS.FIND_EXTERNAL_SCREEN })"
                        >
                            {{ $t('controller.player_position_windows') }}
                        </button>

                        <button
                            class="btn btn-xs btn-outline-primary"
                            v-tooltip="$t('controller.player_fullscreen_tooltip')"
                            @click="setPlayerDisplaySettings({ fullscreen: PLAYER_FULLSCREEN_DISPLAY_SETTINGS.TOGGLE })"
                        >
                            {{ $t('controller.player_fullscreen') }}
                        </button>
                    </div>
                </div>
            </div>

            <div class="col-12 col-md-8">
                <ceremony-controller
                    :with-status="true"
                    :ceremony_id="ceremonyId"
                    :communication="PLAYER_COMMUNICATION.IPC"
                >
                    <template #button-script="{ ceremony }">
                        <button
                            v-tooltip="$t('ceremony.save_pdf_tooltip', {type: ceremony.translated_type})"
                            class="btn btn-xs btn-outline-primary ml-3"
                            @click="saveCeremonyPdf(ceremony)"
                        >
                            {{ $t('ceremony.script') }}
                        </button>
                    </template>
                </ceremony-controller>
            </div>
        </div>
    </div>
</template>

<script setup>

    import CeremonyController from '../../controller/components/CeremonyController.vue';
    import Player from '../../player-v2/components/Player.vue';
    import { PLAYER_COMMUNICATION } from '../../enums/player-communication';
    import { RENDER_MODE } from '../../enums/render-mode';
    import { IpcCall, ipcCall } from '../utils/ipcCall';
    import { PLAYER_FULLSCREEN_DISPLAY_SETTINGS } from '../enums/player-display-settings-enum';
    import { PLAYER_POSITION_DISPLAY_SETTINGS } from '../enums/player-display-settings-enum';
    import { ref } from 'vue';
    import FullscreenLoading from '../../components/FullscreenLoading.vue';
    import { notifyError, notifySuccess } from '../../utils/notify';
    import i18n from '../../utils/locale';

    defineProps({
        ceremonyId: {
            type: Number,
            required: true,
        },
    });

    const isLoading = ref(false);

    async function setPlayerDisplaySettings(settings) {
        isLoading.value = true;

        try {
            await ipcCall(IpcCall.SetPlayerDisplaySettings, settings);
            notifySuccess(i18n.t('controller.positioned_windows'));
        } catch (exception) {
            notifyError(exception);
        }

        isLoading.value = false;
    }

    async function saveCeremonyPdf(ceremony) {
        isLoading.value = true;

        try {
            await ipcCall(IpcCall.SaveCeremonyPdfExport, {
                ceremonyId: ceremony.id,
                name: ceremony.name,
                date: ceremony.date
            });

            notifySuccess(i18n.t('ceremony.saved_ceremony_pdf', { type: ceremony.translated_type }));
        } catch (exception) {
            notifyError(exception, i18n.t('general.something_went_wrong'));
        } finally {
            isLoading.value = false;
        }
    }
</script>

<style lang="scss" scoped>

    .player-preview-wrapper {
        position: sticky;
        top: 15px;

        .player-preview {
            max-height: 100%;
        }
    }

</style>
